import { Suspense, lazy } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'

import { useCurrentUser } from './api/auth'
import { AppLayout } from './components/AppLayout'
import { LoginPage } from './pages/LoginPage'

// Import estático sólo para lo que se ve antes de loguearse — es la
// primera pantalla que ve casi toda sesión nueva, no tiene sentido
// sumarle el parpadeo de un Suspense a eso en particular. El resto de
// las ~20 páginas se separa en su propio chunk (auditoría de
// performance: antes se cargaban todas de una, aunque la visita fuera
// sólo a /bots) — `.then(m => ({ default: m.X }))` porque cada página
// exporta con nombre, no default, y `lazy` sólo entiende default.
const ResetPasswordPage = lazy(() =>
  import('./pages/ResetPasswordPage').then((m) => ({ default: m.ResetPasswordPage })),
)
const AlertsPage = lazy(() => import('./pages/AlertsPage').then((m) => ({ default: m.AlertsPage })))
const BotCalendarPage = lazy(() =>
  import('./pages/BotCalendarPage').then((m) => ({ default: m.BotCalendarPage })),
)
const BotDetailPage = lazy(() =>
  import('./pages/BotDetailPage').then((m) => ({ default: m.BotDetailPage })),
)
const BotsHistoryPage = lazy(() =>
  import('./pages/BotsHistoryPage').then((m) => ({ default: m.BotsHistoryPage })),
)
const BotTransactionsPage = lazy(() =>
  import('./pages/BotTransactionsPage').then((m) => ({ default: m.BotTransactionsPage })),
)
const BotsPage = lazy(() => import('./pages/BotsPage').then((m) => ({ default: m.BotsPage })))
const DashboardPage = lazy(() =>
  import('./pages/DashboardPage').then((m) => ({ default: m.DashboardPage })),
)
const ComparePage = lazy(() => import('./pages/ComparePage').then((m) => ({ default: m.ComparePage })))
const DatasetsPage = lazy(() =>
  import('./pages/DatasetsPage').then((m) => ({ default: m.DatasetsPage })),
)
const ExchangeAccountDetailPage = lazy(() =>
  import('./pages/ExchangeAccountDetailPage').then((m) => ({ default: m.ExchangeAccountDetailPage })),
)
const ExchangesPage = lazy(() =>
  import('./pages/ExchangesPage').then((m) => ({ default: m.ExchangesPage })),
)
const HelpPage = lazy(() => import('./pages/HelpPage').then((m) => ({ default: m.HelpPage })))
const JournalPage = lazy(() =>
  import('./pages/JournalPage').then((m) => ({ default: m.JournalPage })),
)
const MatrixPage = lazy(() => import('./pages/MatrixPage').then((m) => ({ default: m.MatrixPage })))
const ResultPage = lazy(() => import('./pages/ResultPage').then((m) => ({ default: m.ResultPage })))
const TaxReportPage = lazy(() =>
  import('./pages/TaxReportPage').then((m) => ({ default: m.TaxReportPage })),
)
const RunDetailPage = lazy(() =>
  import('./pages/RunDetailPage').then((m) => ({ default: m.RunDetailPage })),
)
const RunsPage = lazy(() => import('./pages/RunsPage').then((m) => ({ default: m.RunsPage })))
const StrategiesPage = lazy(() =>
  import('./pages/StrategiesPage').then((m) => ({ default: m.StrategiesPage })),
)
const StrategyPage = lazy(() =>
  import('./pages/StrategyPage').then((m) => ({ default: m.StrategyPage })),
)
const SystemSettingsPage = lazy(() =>
  import('./pages/SystemSettingsPage').then((m) => ({ default: m.SystemSettingsPage })),
)
const SystemHealthPage = lazy(() =>
  import('./pages/SystemHealthPage').then((m) => ({ default: m.SystemHealthPage })),
)
const UsersPage = lazy(() =>
  import('./pages/UsersPage').then((m) => ({ default: m.UsersPage })),
)
const TrashPage = lazy(() =>
  import('./pages/TrashPage').then((m) => ({ default: m.TrashPage })),
)
const WalkForwardPage = lazy(() =>
  import('./pages/WalkForwardPage').then((m) => ({ default: m.WalkForwardPage })),
)

//: Mismo look que la pantalla de "Cargando…" de más abajo (sesión
//: todavía resolviéndose) — un chunk que tarda en bajar es el mismo
//: tipo de espera para quien mira, no amerita un spinner distinto.
function RouteFallback() {
  return (
    <div className="centered-screen">
      <span className="muted">Cargando…</span>
    </div>
  )
}

// Guarda la URL que se quería visitar en el `state` de la navegación a
// /login — como el login pasa en esa MISMA entrada de historial (nada
// navega mientras se resuelve), sigue disponible en location.state una vez
// que `user` pasa a existir y el fallback de abajo decide adónde mandar.
// Sin esto, cualquier link directo a una pantalla puntual (compartido,
// favorito del navegador) caía siempre en /bots después de loguearse.
function RedirectToLogin() {
  const location = useLocation()
  const returnTo = `${location.pathname}${location.search}`
  return <Navigate to="/login" state={{ returnTo }} replace />
}

// Contraparte de `RedirectToLogin`: si la navegación a /login trajo un
// returnTo (URL puntual que se quería visitar), lo usa una sola vez en vez
// del /bots por defecto. Sigue siendo el "atrapa cualquier otra cosa" del
// árbol logueado (una ruta vieja, /login con sesión ya activa).
function LoggedInFallback() {
  const location = useLocation()
  const state = location.state as { returnTo?: string } | null
  return <Navigate to={state?.returnTo || '/dashboard'} replace />
}

export function App() {
  const { data: user, isPending, isError, refetch, isRefetching } = useCurrentUser()

  if (isPending) {
    return (
      <div className="centered-screen">
        <span className="muted">Cargando…</span>
      </div>
    )
  }

  // `useCurrentUser` sólo convierte un 401 en `data: null` (sesión real-
  // mente inexistente) — cualquier OTRO fallo (500, corte de red) llega
  // acá como `isError`, y sin este chequeo `!user` lo trataba igual que
  // "no hay sesión" y mandaba a un usuario logueado a la pantalla de
  // login sin ninguna explicación.
  if (isError) {
    return (
      <div className="centered-screen">
        <div className="alert" role="alert">
          No se pudo verificar la sesión. Puede ser un problema de conexión.
        </div>
        <button type="button" className="button" onClick={() => refetch()} disabled={isRefetching}>
          {isRefetching ? 'Reintentando…' : 'Reintentar'}
        </button>
      </div>
    )
  }

  if (!user) {
    return (
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="*" element={<RedirectToLogin />} />
        </Routes>
      </Suspense>
    )
  }

  return (
    <AppLayout>
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          {/* Dashboard es la vista por defecto: es lo primero que alguien quiere
              ver al entrar (gráficos y análisis de la flota) — Bots (la lista
              de bots en ejecución) y la matriz de investigación quedaron como
              sus propias rutas. */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/matriz" element={<MatrixPage />} />
          <Route path="/resultados/:cacheKey" element={<ResultPage />} />
          <Route path="/comparador" element={<ComparePage />} />
          <Route path="/runs" element={<RunsPage />} />
          <Route path="/runs/:runId" element={<RunDetailPage />} />
          <Route path="/datos" element={<DatasetsPage />} />
          <Route path="/estrategias" element={<StrategiesPage />} />
          <Route path="/estrategias/:slug" element={<StrategyPage />} />
          <Route path="/walk-forward" element={<WalkForwardPage />} />
          <Route path="/exchanges" element={<ExchangesPage />} />
          <Route path="/exchanges/:accountId" element={<ExchangeAccountDetailPage />} />
          <Route path="/bots" element={<BotsPage />} />
          <Route path="/bots/historial" element={<BotsHistoryPage />} />
          <Route path="/bots/transacciones" element={<BotTransactionsPage />} />
          <Route path="/bots/calendario" element={<BotCalendarPage />} />
          <Route path="/alertas" element={<AlertsPage />} />
          <Route path="/journal" element={<JournalPage />} />
          <Route path="/reporte-fiscal" element={<TaxReportPage />} />
          <Route path="/bots/:botId" element={<BotDetailPage />} />
          <Route path="/configuracion" element={<SystemSettingsPage />} />
          <Route path="/papelera" element={<TrashPage />} />
          {user.is_admin && <Route path="/sistema" element={<SystemHealthPage />} />}
          {/* Registros se unificó con Salud del sistema: los enlaces viejos siguen funcionando. */}
          {user.is_admin && (
            <Route path="/sistema/registros" element={<Navigate to="/sistema" replace />} />
          )}
          {user.is_admin && <Route path="/sistema/usuarios" element={<UsersPage />} />}
          {user.is_admin && <Route path="/sistema/papelera" element={<TrashPage />} />}
          <Route path="/ayuda" element={<HelpPage />} />
          <Route path="*" element={<LoggedInFallback />} />
        </Routes>
      </Suspense>
    </AppLayout>
  )
}
